import { Button } from '~/components/common/Button'
import { LoadingState } from '~/components/common/LoadingSpinner'
import { TaskForm } from '~/components/task/TaskForm'
import { Typography } from '~/components/common/Typography'
import { TaskParams } from '~/components/task-forms/BaseTaskForm'
import { MuleRefactorFlowScreen } from '~/components/mule-refactor'
import { SpringIntegrationRefactorFlowScreen } from '~/components/spring-integration-refactor'
import { CommandType, UsedCommandType } from '~/constants/commands'
import { useSidebarContext } from '~/contexts/SidebarContext'
import { getTaskRequest, useTask } from '~/contexts/TaskContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { useConversationUuid } from '~/hooks'
import { useCompactViewport } from '~/hooks/useCompactViewport'
import {
  AgentRequest,
  AgentTaskStatus,
  SourceOutputExamples,
  agentTaskStatusFromJSON,
  commandTypeFromJSON,
  sourceOutputExampleTypeFromJSON
} from '~/protos/agents'
import { submitTaskUpdate } from '~/services/taskService'
import { TaskObject } from '~/types/TaskType'
import { BetterToast } from '~/utils/toast'
import { CreditLimitExceededMessage } from '../common/CreditLimitExceededMessage'
import TaskResultsView from './TaskResultsView'

// Helper to safely convert CommandType to UsedCommandType
function getCommandTypeFromTask(commandType: string): UsedCommandType | null {
  const taskCommandType = commandTypeFromJSON(commandType)

  if (
    taskCommandType !== CommandType.NONE &&
    taskCommandType !== CommandType.UNRECOGNIZED &&
    taskCommandType !== CommandType.FIX_PR
  ) {
    return taskCommandType as UsedCommandType
  }
  return null
}

// Helper to extract TaskParams from task data
export function extractTaskParamsFromTask(task: TaskObject): TaskParams | null {
  const originalRequestData = getTaskRequest(task)

  if (!originalRequestData) return null

  try {
    // Map and convert field types properly for AgentRequest
    const mappedData = {
      ...originalRequestData,
      // Convert string IDs to numbers
      userId: originalRequestData.userId ? Number(originalRequestData.userId) : 0,
      orgId: originalRequestData.orgId ? Number(originalRequestData.orgId) : 0,
      // Map conversationId to uuid if present, ensure it's a string
      uuid: String(originalRequestData.conversationId || originalRequestData.uuid || ''),
      // Remove conversationId to avoid conflicts
      conversationId: undefined,
      // Convert sourceOutputExamples sourceDataType from string to enum
      sourceOutputExamples: originalRequestData.sourceOutputExamples?.map(
        (example: SourceOutputExamples) => ({
          ...example,
          sourceDataType:
            typeof example.sourceDataType === 'string'
              ? sourceOutputExampleTypeFromJSON(example.sourceDataType)
              : example.sourceDataType
        })
      )
    }

    const agentRequest = AgentRequest.fromPartial(mappedData)

    return { request: agentRequest }
  } catch (error) {
    console.error('Error converting task data:', error)
    console.error('Original data:', originalRequestData)
    return null
  }
}

// Simple status color helper
export function getStatusColor(status: string) {
  const statusUpper = status.toUpperCase()
  switch (statusUpper) {
    case 'INIT':
      return 'text-blue-600 bg-blue-100'
    case 'COMPLETED':
    case 'SUCCESS':
      return 'text-green-600 bg-green-100'
    case 'FAILED':
    case 'ERROR':
      return 'text-red-600 bg-red-100'
    case 'RUNNING':
    case 'IN_PROGRESS':
      return 'text-yellow-600 bg-yellow-100'
    default:
      return 'text-gray-600 bg-gray-100'
  }
}

// TaskForm wrapper for task editing
function TaskFormWrapper({
  task,
  command,
  onClose
}: {
  task: TaskObject
  command: UsedCommandType
  onClose: () => void
}) {
  const taskParams = extractTaskParamsFromTask(task)
  const uuid = useConversationUuid()
  const { currentWorkspace } = useWorkspaceContext()

  const handleTaskUpdate = async (updatedTaskParams: TaskParams): Promise<boolean> => {
    if (!updatedTaskParams?.request) {
      BetterToast.error('Form is incomplete')
      return false
    }

    if (!currentWorkspace?.uuid) {
      BetterToast.error('Workspace not found')
      return false
    }

    try {
      const result = await submitTaskUpdate({
        workspaceUuid: currentWorkspace.uuid,
        uuid,
        taskId: task.task_id,
        status: AgentTaskStatus.INIT,
        request: AgentRequest.toJSON(updatedTaskParams.request) as Record<string, unknown>
      })

      if (result?.status === 'success') {
        BetterToast.success('Task submitted successfully!')
        return true
      } else {
        BetterToast.error('Failed to submit task')
        return false
      }
    } catch (error) {
      console.error('Task submission failed:', error)
      BetterToast.error(
        error instanceof Error
          ? `Failed to submit task: ${error.message}`
          : 'Failed to submit task. Please try again.'
      )
      return false
    }
  }

  return (
    <TaskForm
      command={command}
      withExamplePrefilled={false}
      closeTask={onClose}
      className='h-full'
      key={`task-${task.task_id}`}
      initialTaskData={taskParams}
      isEditingTask={true}
      onTaskSubmit={handleTaskUpdate}
    />
  )
}

interface TaskDisplayProps {
  onClose: () => void
}

export function TaskDisplay({ onClose }: TaskDisplayProps) {
  const isCompactViewport = useCompactViewport()
  const { collapsed } = useSidebarContext()
  const { task, isLoading, error, isResultVisible } = useTask()
  if (isLoading) {
    return <LoadingState message='Loading task...' className='h-full' />
  }

  // Error or not found states
  if (error || !task) {
    return (
      <div className='flex items-center justify-center h-full'>
        <div className='text-center'>
          <h3 className='text-lg font-medium text-gray-900 mb-2'>
            {error ? 'Error Loading Task' : 'Task Not Found'}
          </h3>
          <p className='text-gray-600 mb-4'>
            {error
              ? typeof error === 'object' && error !== null && 'message' in error
                ? (error as Error).message
                : 'An error occurred'
              : `Task could not be found.`}
          </p>
          <button
            onClick={onClose}
            className='px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors'
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  // Show credit limit exceeded message if task result is not visible
  if (!isResultVisible) {
    return <CreditLimitExceededMessage />
  }

  // check if it should show workspace
  const taskStatus = agentTaskStatusFromJSON(task.status)

  // Check command type for task-specific rendering
  const taskCommandType = commandTypeFromJSON(task.cmd_type)
  const isMuleRefactorTask = taskCommandType === CommandType.MULE_REFACTORING
  const isSpringIntegrationRefactorTask =
    taskCommandType === CommandType.SPRING_INTEGRATION_REFACTORING

  // INIT status - show form for completion.
  if (task.status.toUpperCase() === 'INIT') {
    const command = getCommandTypeFromTask(task.cmd_type)

    if (!command) {
      return (
        <div className='flex items-center justify-center h-full'>
          <div className='text-center max-w-md p-8'>
            <div className='text-yellow-500 mb-4'>
              <svg
                className='w-12 h-12 mx-auto'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 19c-.77.833.192 2.5 1.732 2.5z'
                />
              </svg>
            </div>
            <h3 className='text-lg font-semibold text-gray-900 mb-2'>Unsupported Command Type</h3>
            <p className='text-gray-600 mb-4'>
              Command type &apos;{task.cmd_type}&apos; is not supported for editing.
            </p>
            <button
              onClick={onClose}
              className='px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors'
            >
              Back to Conversation
            </button>
          </div>
        </div>
      )
    }

    // INIT-status refactor tasks cannot be edited via the generic
    // TaskFormWrapper. The wrapper reconstructs an AgentRequest via
    // ``extractTaskParamsFromTask`` which does NOT round-trip the multi-repo
    // / auxiliary-code-context payload these refactor flows depend on, so
    // falling through would silently render a partially-broken generic form
    // with no indication that something's wrong. Explicit guard with an
    // actionable message is better than silent breakage. The supported
    // entry point for these flows is the landing-page tile in
    // WorkspaceHomePage; "reopen partially-created refactor task" is not
    // a designed scenario today.
    if (isMuleRefactorTask || isSpringIntegrationRefactorTask) {
      const label = isMuleRefactorTask ? 'Mule Refactor' : 'Spring Integration Refactor'
      return (
        <div
          className='flex items-center justify-center h-full'
          data-testid='refactor-init-not-supported'
        >
          <div className='text-center max-w-md p-8'>
            <div className='text-tokens-status-warning-1 mb-4'>
              <svg
                className='w-12 h-12 mx-auto'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 19c-.77.833.192 2.5 1.732 2.5z'
                />
              </svg>
            </div>
            <Typography
              fontFamily='geist'
              fontSize='titleSm'
              fontWeight='bold'
              className='text-tokens-neutral-text-primary mb-2'
            >
              Cannot edit unsubmitted {label} task
            </Typography>
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              className='text-tokens-neutral-text-secondary mb-4'
            >
              {label} tasks need their multi-repository inputs to be set up from the workspace home
              tile. Reopening an unsubmitted task from the sidebar is not currently supported.
              Please go back and start a new {label} task.
            </Typography>
            <Button variant='primary' onClick={onClose}>
              Back to Workspace
            </Button>
          </div>
        </div>
      )
    }

    return <TaskFormWrapper task={task} command={command} onClose={onClose} />
  }

  // For Mule Refactor tasks, render the flow screen instead of timeline
  if (isMuleRefactorTask) {
    return <MuleRefactorFlowScreen taskStatus={taskStatus} taskId={task.task_id} />
  }

  // For Spring Integration Refactor tasks, render the same flow screen pattern.
  // SpringIntegrationRefactorFlowScreen is currently a re-export of MuleRefactorFlowScreen,
  // so its prop surface is exactly { taskStatus, taskId } — extra props (inline / onExit)
  // would be silently dropped. Match the Mule call site shape until the components diverge.
  if (isSpringIntegrationRefactorTask) {
    return <SpringIntegrationRefactorFlowScreen taskStatus={taskStatus} taskId={task.task_id} />
  }

  // Non-INIT status - show new results view
  return (
    <div className='h-full flex flex-col bg-[#fbfbfb]'>
      {/* Back navigation - always outside scroll */}
      <div
        className={
          isCompactViewport
            ? 'flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-white shrink-0'
            : 'h-[52px] px-6 border-b border-gray-200 bg-white shrink-0 flex items-center'
        }
      >
        <div
          className={
            isCompactViewport
              ? 'flex items-center justify-between'
              : `flex items-center transition-transform duration-500 ease-in-out ${
                  collapsed ? 'translate-x-[132px]' : 'translate-x-0'
                }`
          }
        >
          <button
            onClick={onClose}
            className='inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors shrink-0'
          >
            <span className='flex h-5 w-5 items-center justify-center shrink-0'>
              <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M15 19l-7-7 7-7'
                />
              </svg>
            </span>
            <Typography
              fontFamily='geist'
              fontSize='titleSm'
              fontWeight='medium'
              className='text-inherit leading-none'
            >
              Back
            </Typography>
          </button>
        </div>
      </div>

      <div
        className={
          isCompactViewport
            ? 'flex flex-col items-center gap-3 p-2 flex-1 min-h-0 overflow-hidden bg-[#fbfbfb]'
            : 'flex flex-col items-center gap-[24px] p-[24px] flex-1 min-h-0 overflow-hidden bg-[#fbfbfb]'
        }
      >
        <TaskResultsView readOnly={true} />
      </div>
    </div>
  )
}
